import {
  ApiProperty,
  ApiPropertyOptional,
  IntersectionType,
} from '@nestjs/swagger';
import { JobPhaseInterface } from '../interfaces/job-phase.interface';
import { CommonEntityDto } from '../../common/dto/common-entity.dto';
import {
  IsBoolean,
  IsEnum,
  IsISO8601,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  Max,
  MaxLength,
  Min,
  MinLength,
  ValidateNested,
} from 'class-validator';
import { Exclude, Expose, Type } from 'class-transformer';
import {
  JOB_PHASE_COST_TOTAL_DECIMAL_PLACES,
  JOB_PHASE_COST_TOTAL_MAX,
  JOB_PHASE_COST_TOTAL_MIN,
  JOB_PHASE_NOTES_MIN_LENGTH,
  JOB_PHASE_NOTES_MAX_LENGTH,
} from '../constants/job-phase-validation.constants';
import { GeographyPointDto } from '../../common/dto/geography-point.dto';
import { MaxDecimalPlaces } from '../../common/validators/max-decimal-places.validator';
import { JobDto } from '../../job/dto/job.dto';
import { PhaseDto } from '../../phase/dto/phase.dto';
import { ContractorDto } from '../../contractor/dto/contractor.dto';
import { UserDto } from '../../user/dto/user.dto';
import { JobPhaseContractorHistoryDto } from '../../job-phase-contractor-history/dto/job-phase-contractor-history.dto';
import { FlowRuleCommonDto } from '../../flow-rule-common/dto/flow-rule-common.dto';
import { JobPhaseBumpDto } from '../../job-phase-bump/dtos/job-phase-bump.dto';
import { JobPhaseReadinessEnum } from '../enums/job-phase-readiness.enum';
import { JobPhaseReadyHourEnum } from '../enums/job-phase-ready-hour.enum';
import { GeoMatchStatusEnum } from '../../common/enums/geo-match-status.enum';

@Exclude()
export class JobPhaseDto
  extends IntersectionType(CommonEntityDto, FlowRuleCommonDto)
  implements JobPhaseInterface
{
  @ApiProperty({
    title: 'Priority',
    description: 'The priority stamped from the phase at time of creation',
  })
  @IsNumber()
  @Expose()
  priority!: number;

  @ApiProperty({
    title: 'RequestDelayDays',
    description: 'Request Delay Days',
  })
  @IsNumber()
  @Expose()
  requestDelayDays!: number;

  @ApiProperty({
    type: 'string',
    nullable: true,
    format: 'date-time',
    title: 'Date Request Due',
    description: 'The date and time the job phase should start',
  })
  @IsISO8601({ strict: true })
  @Expose()
  dateRequestDue!: Date | null;

  @ApiProperty({
    type: 'boolean',
    default: 'true',
    title: 'Required?',
    description: 'Will be true if this phase is required for the job',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  required!: boolean;

  @ApiProperty({
    type: 'boolean',
    default: 'false',
    title: 'Requested?',
    description: 'Will be true if contractor has been requested for this phase',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  requested!: boolean;

  @ApiProperty({
    type: 'boolean',
    default: 'false',
    title: 'Trim Option Selected?',
    description:
      'Will be true if the trim option was selected for this phase in the Request Resource/Log modal',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  trimOptionSelected!: boolean;

  @ApiProperty({
    type: 'enum',
    enum: JobPhaseReadyHourEnum,
    nullable: true,
    title: 'Spray Ready Hour',
    description: 'The time when the contractor can start.',
  })
  @IsEnum(JobPhaseReadyHourEnum)
  @Expose()
  readyHour!: JobPhaseReadyHourEnum | null;

  @ApiProperty({
    type: 'string',
    nullable: true,
    format: 'date-time',
    title: 'Date Requested',
    description: 'The date the job phase resource was requested',
  })
  @IsISO8601({ strict: true })
  @Expose()
  dateRequested!: Date | null;

  @ApiProperty({
    type: 'string',
    nullable: true,
    format: 'date-time',
    title: 'Date Requested',
    description: 'The date and time the job phase resource was requested',
  })
  @IsISO8601({ strict: true })
  @Expose()
  dateTimeRequested!: Date | null;

  @ApiProperty({
    type: 'string',
    nullable: true,
    format: 'date-time',
    title: 'Date Start Count',
    description: 'The date and time the job phase day count should start.',
  })
  @IsISO8601({ strict: true })
  @Expose()
  dateStartCount!: Date;

  @ApiProperty({
    type: 'string',
    nullable: true,
    format: 'date-time',
    title: 'Date Start',
    description: 'The date and time the job phase should start',
  })
  @IsISO8601({ strict: true })
  @Expose()
  dateStart!: Date | null;

  @ApiProperty({
    type: 'string',
    nullable: true,
    format: 'date-time',
    title: 'Custom Date Start',
    description: 'Custom Date Start',
  })
  @IsISO8601({ strict: true })
  @Expose()
  customDateStart!: Date | null;

  @ApiProperty({
    type: 'number',
    title: 'Custom Date Start Count',
    description: 'Custom Date Start Count',
  })
  @Expose()
  customDateStartCount!: number;

  @ApiProperty({
    type: 'string',
    nullable: true,
    format: 'date-time',
    title: 'Date Start',
    description: 'The date and time the job phase should start',
  })
  @IsISO8601({ strict: true })
  @Expose()
  initialDateStart!: Date | null;

  @ApiProperty({
    type: 'string',
    nullable: true,
    format: 'date-time',
    title: 'Date Due',
    description: 'The date and time the job phase is expected to be complete',
  })
  @IsISO8601({ strict: true })
  @Expose()
  dateDue!: Date | null;

  @ApiProperty({
    type: 'string',
    nullable: true,
    format: 'date-time',
    title: 'Date Approved',
    description: 'The date and time the job phase was approved as complete',
  })
  @IsISO8601({ strict: true })
  @Expose()
  dateApproved!: Date | null;

  @ApiProperty({
    type: 'string',
    nullable: true,
    title: 'Notes',
    description: 'Notes related to this Job Phase',
    minLength: JOB_PHASE_NOTES_MIN_LENGTH,
    maxLength: JOB_PHASE_NOTES_MAX_LENGTH,
  })
  @IsString()
  @MinLength(JOB_PHASE_NOTES_MIN_LENGTH)
  @MaxLength(JOB_PHASE_NOTES_MAX_LENGTH)
  @Expose()
  notes!: string | null;

  @ApiProperty({
    type: 'boolean',
    default: false,
    title: 'Approved?',
    description: 'True when the job phase has been approved.',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  approved!: boolean;

  @ApiProperty({
    type: () => GeographyPointDto,
    nullable: true,
    default: null,
    title: 'Approved Geo',
    description: 'GPS location of device when the job phase was approved.',
  })
  @ValidateNested()
  @Type(() => GeographyPointDto)
  @Expose()
  approvedGeo!: GeographyPointDto | null;

  @ApiPropertyOptional({
    title: 'Approved Geo Accuracy',
    description:
      'Accuracy in meters of the GPS reading when the job phase was approved.',
    nullable: true,
  })
  @IsOptional()
  @IsNumber()
  @Min(0)
  @Expose()
  approvedGeoAccuracy!: number | null;

  @ApiPropertyOptional({
    type: 'enum',
    enum: GeoMatchStatusEnum,
    nullable: true,
    title: 'Approved Geo Status',
    description: 'Geo match status for the approved GPS location.',
  })
  @IsOptional()
  @IsEnum(GeoMatchStatusEnum)
  @Expose()
  approvedGeoStatus!: GeoMatchStatusEnum | null;

  @ApiPropertyOptional({
    title: 'Approved Geo Calculated Distance',
    description:
      'Distance in meters between the approved GPS point and the job geo.',
    nullable: true,
  })
  @IsOptional()
  @IsNumber()
  @Expose()
  approvedGeoCalculatedDistance!: number | null;

  @ApiProperty({
    type: 'string',
    format: 'uuid',
    title: 'Associated Job ID',
  })
  @IsUUID()
  @Expose()
  jobId!: string;

  @ApiProperty({
    type: 'string',
    format: 'uuid',
    title: 'Associated Phase ID',
  })
  @IsUUID()
  @Expose()
  phaseId!: string;

  @ApiProperty({
    type: 'string',
    nullable: true,
    format: 'uuid',
    title: 'Associated Contractor ID',
  })
  @IsUUID()
  @Expose()
  contractorId!: string | null;

  @ApiProperty({
    type: 'string',
    nullable: true,
    format: 'uuid',
    title: 'Suggested Contractor ID',
  })
  @IsUUID()
  @Expose()
  suggestedContractorId!: string | null;

  @ApiProperty({
    type: 'string',
    nullable: true,
    format: 'uuid',
    title: 'Initial Contractor ID',
  })
  @IsUUID()
  @Expose()
  initialContractorId!: string | null;

  @ApiProperty({
    type: 'string',
    nullable: true,
    format: 'uuid',
    title: 'Associated Approving User ID',
  })
  @IsUUID()
  @Expose()
  approvingUserId!: string | null;

  @ApiProperty({
    type: 'string',
    nullable: true,
    format: 'uuid',
    title: 'Associated Approving User ID',
  })
  @IsUUID()
  @Expose()
  requestingUserId!: string | null;

  @ApiPropertyOptional({
    type: () => UserDto,
    title: 'Associated Approving User',
    description: 'The approving user associated with this job phase',
  })
  @Expose()
  requestingUser?: UserDto;

  @ApiPropertyOptional({
    type: () => JobDto,
    title: 'Associated Job',
    description: 'The job associated with this job phase',
  })
  @Expose()
  job?: JobDto;

  @ApiPropertyOptional({
    type: () => PhaseDto,
    title: 'Associated Phase',
    description: 'The phase associated with this job phase',
  })
  @Expose()
  phase?: PhaseDto;

  @ApiPropertyOptional({
    type: () => ContractorDto,
    title: 'Associated Contractor',
    description: 'The contractor associated with this job phase',
  })
  @Expose()
  contractor?: ContractorDto;

  @ApiPropertyOptional({
    type: () => ContractorDto,
    title: 'Initial Contractor',
    description: 'The initial contractor associated with this job phase',
  })
  @Expose()
  initialContractor?: ContractorDto;

  @ApiPropertyOptional({
    type: () => [JobPhaseContractorHistoryDto],
    title: 'Job phase contractor histories',
    description:
      'The job phase contractor histories associated with this job phase',
  })
  @Expose()
  jobPhaseContractorHistories?: JobPhaseContractorHistoryDto[];

  @ApiPropertyOptional({
    type: () => UserDto,
    title: 'Associated Approving User',
    description: 'The approving user associated with this job phase',
  })
  @Expose()
  approvingUser?: UserDto;

  @ApiPropertyOptional({
    type: () => JobPhaseBumpDto,
    title: 'Associated Job Phase Bump',
    description: 'The job phase bump associated with this job phase',
  })
  @Expose()
  jobPhaseBump?: JobPhaseBumpDto;

  @ApiProperty({
    type: 'number',
    nullable: true,
    title: 'Total Cost',
    description:
      'Total Cost entered on the Prod Payables MS: the final amount the phase is paid with, zero or greater, up to two decimal places, at most 99,999.99. Null clears it and the row goes back to its calculated amount.',
  })
  @IsNumber()
  @MaxDecimalPlaces(JOB_PHASE_COST_TOTAL_DECIMAL_PLACES)
  @Min(JOB_PHASE_COST_TOTAL_MIN)
  @Max(JOB_PHASE_COST_TOTAL_MAX)
  @Expose()
  costTotal!: number;

  @ApiProperty({
    type: 'string',
    nullable: true,
    title: 'Check Number',
    description: 'The check number of which the job phase contractor was paid',
  })
  @Expose()
  checkNumber!: string;

  @ApiProperty({
    type: 'string',
    nullable: true,
    format: 'date-time',
    title: 'Date Paid',
    description: 'The date and time the job phase contractor was paid',
  })
  @IsISO8601({ strict: true })
  @Expose()
  datePaid!: Date | null;

  @ApiProperty({
    type: 'string',
    nullable: true,
    format: 'date-time',
    title: 'Date Assigned',
    description: 'The date the job phase was assigned',
  })
  @IsISO8601({ strict: true })
  @Expose()
  dateAssigned!: Date | null;

  @ApiProperty({
    type: 'enum',
    enum: JobPhaseReadinessEnum,
    nullable: true,
    title: 'Spray Readiness Status',
    description:
      'Indicates the current readiness status for the spraying job phase',
  })
  @IsEnum(JobPhaseReadinessEnum)
  @Expose()
  readinessStatus!: JobPhaseReadinessEnum | null;

  @ApiProperty({
    type: 'number',
    nullable: true,
    title: 'PayableTotal',
    description: 'The PayableTotal of the job phase',
  })
  @IsNumber()
  @Expose()
  payableTotal!: number;

  @ApiProperty({
    type: 'number',
    nullable: true,
    title: 'payableReceiptTotal',
    description: 'The payableReceiptTotal of the job phase',
  })
  @IsNumber()
  @Expose()
  payableReceiptTotal!: number;
}
